/**
 * Feb 2026 · Low Stock Alerts.
 *
 * Every Raw Material whose `current_stock < min_stock` — the drill-in
 * target from the "Critical Stock Alerts" tile on the Executive Dashboard.
 * Ordered by shortage severity (biggest gap first) so procurement can
 * triage top-down.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  WarningIcon, MagnifyingGlassIcon, ArrowsClockwiseIcon,
  ArrowLeftIcon, PackageIcon, ClipboardTextIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

export default function LowStockAlerts() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: mats }, { data: sups }] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
      ]);
      const low = (mats || [])
        .filter(m => m.is_active !== false)
        .filter(m => Number(m.current_stock || 0) < Number(m.min_stock || 0))
        .map(m => ({
          ...m,
          shortage: Number(m.min_stock || 0) - Number(m.current_stock || 0),
          coverage_pct: Number(m.min_stock) > 0
            ? Math.round((Number(m.current_stock || 0) / Number(m.min_stock)) * 100)
            : 0,
        }))
        .sort((a, b) => b.shortage - a.shortage);
      setRows(low);
      setSuppliers(Object.fromEntries((sups || []).map(s => [s.id, s])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load low-stock alerts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => [r.code, r.name, r.category, suppliers[r.supplier_id]?.name]
      .filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [rows, q, suppliers]);

  const totalShortage = filtered.reduce((s, r) => s + r.shortage, 0);
  const criticalCount = filtered.filter(r => r.current_stock <= 0).length;

  const exportCsv = () => {
    exportToCSV("low-stock-alerts.csv", filtered.map(r => ({
      code: r.code, name: r.name, category: r.category,
      current: r.current_stock, min: r.min_stock, shortage: r.shortage,
      uom: r.uom, supplier: suppliers[r.supplier_id]?.name || "",
    })), [
      { key: "code", label: "RM Code" }, { key: "name", label: "Name" },
      { key: "category", label: "Category" }, { key: "current", label: "Current" },
      { key: "min", label: "Min" }, { key: "shortage", label: "Shortage" },
      { key: "uom", label: "UoM" }, { key: "supplier", label: "Supplier" },
    ]);
  };

  const severityCls = (pct) => (
    pct <= 0     ? "bg-red-500/15 text-red-300 border-red-500/40" :
    pct < 50     ? "bg-red-500/10 text-red-300 border-red-500/30" :
    pct < 100    ? "bg-amber-500/10 text-amber-300 border-amber-500/30" :
                   "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
  );

  return (
    <div className="p-6 space-y-4" data-testid="low-stock-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Link to="/" className="hover:text-white flex items-center gap-1">
              <ArrowLeftIcon size={10} weight="bold" />Dashboard
            </Link>
            <span>·</span>
            <span>Inventory · Alerts</span>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3">
            <WarningIcon size={28} weight="fill" className="text-red-400" />
            Low Stock Alerts
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Every raw material below its minimum stock level · biggest shortage first · restock before production issue is blocked
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="low-stock-refresh" onClick={load} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
          </button>
          <button data-testid="low-stock-export" onClick={exportCsv} disabled={filtered.length === 0} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm disabled:opacity-40 flex items-center gap-2">
            <ClipboardTextIcon size={13} weight="bold" />Export CSV
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="tactical-card p-4 border-l-2 border-l-red-500">
          <div className="overline text-red-300 mb-1">Items Below Minimum</div>
          <div className="font-mono text-3xl font-semibold text-red-400" data-testid="low-stock-count">{rows.length}</div>
        </div>
        <div className="tactical-card p-4 border-l-2 border-l-red-700">
          <div className="overline text-red-300 mb-1">Stock-Out (0 or less)</div>
          <div className="font-mono text-3xl font-semibold text-red-500" data-testid="low-stock-critical">{criticalCount}</div>
        </div>
        <div className="tactical-card p-4">
          <div className="overline mb-1">Total Shortage Qty</div>
          <div className="font-mono text-3xl font-semibold text-white">{totalShortage.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="tactical-card p-4">
          <div className="overline mb-1">Filtered Rows</div>
          <div className="font-mono text-3xl font-semibold text-zinc-300">{filtered.length}</div>
        </div>
      </div>

      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlassIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="low-stock-search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search RM code / name / category / supplier…"
              className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">RM Code</th>
                <th className="px-3 py-2 text-left">Name / Category</th>
                <th className="px-3 py-2 text-right">Current</th>
                <th className="px-3 py-2 text-right">Minimum</th>
                <th className="px-3 py-2 text-right">Shortage</th>
                <th className="px-3 py-2 text-left">Coverage</th>
                <th className="px-3 py-2 text-left">Supplier</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-xs">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-emerald-300 text-xs" data-testid="low-stock-empty">
                  <PackageIcon size={20} weight="bold" className="inline mr-2" />
                  All materials are at or above their minimum stock levels
                </td></tr>
              )}
              {!loading && filtered.map((r, i) => {
                const sup = suppliers[r.supplier_id];
                return (
                  <tr key={r.id} data-testid={`low-stock-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                    <td className="px-3 py-2 font-mono text-[var(--accent)] whitespace-nowrap text-xs">
                      <Link to="/raw-materials" className="hover:underline">{r.code}</Link>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.name}
                      <div className="text-[10px] text-zinc-500">{r.category}</div>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${r.current_stock <= 0 ? "text-red-500" : "text-red-300"}`}>
                      {Number(r.current_stock || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} <span className="text-zinc-500 text-[10px]">{r.uom}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">
                      {Number(r.min_stock || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-red-400" data-testid={`low-stock-shortage-${i}`}>
                      {Number(r.shortage).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${severityCls(r.coverage_pct)}`}>
                        {r.current_stock <= 0 ? "STOCK-OUT" : `${r.coverage_pct}%`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400">
                      {sup ? <>{sup.name}<div className="text-[10px] text-zinc-500 font-mono">{sup.short_code || sup.code || ""}</div></> : <span className="text-zinc-600">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
